import React, { useEffect, useRef, useState } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/**
 * Cloudflare Turnstile widget. Renders a checkbox-style captcha that works
 * in all major browsers including Brave, Firefox-strict, and Safari.
 *
 * Loads the official api.js once per page and renders an explicit widget
 * tied to a sitekey, calling `onVerify(token)` when the visitor passes.
 */
export default function TurnstileWidget({ siteKey, onVerify, theme = 'light', testid = 'turnstile' }) {
  const wrapRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!siteKey) {
      setError('Captcha not configured.');
      return undefined;
    }

    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    const ensureLoaded = () => new Promise((resolve, reject) => {
      if (window.turnstile) { resolve(); return; }
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('Turnstile script failed')), { once: true });
        return;
      }
      const s = document.createElement('script');
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Turnstile script failed'));
      document.head.appendChild(s);
    });

    let cancelled = false;

    ensureLoaded()
      .then(() => {
        if (cancelled) return;
        if (!window.turnstile || !wrapRef.current) return;
        try {
          widgetIdRef.current = window.turnstile.render(wrapRef.current, {
            sitekey: siteKey,
            theme,
            'refresh-expired': 'auto',
            callback: (token) => onVerify && onVerify(token),
            'expired-callback': () => onVerify && onVerify(null),
            'error-callback': () => setError('Captcha failed to load. Please refresh and try again.'),
          });
        } catch (e) {
          setError('Captcha failed to render.');
        }
      })
      .catch((e) => setError(e.message || 'Captcha failed to load'));

    return () => {
      cancelled = true;
      try {
        if (window.turnstile && widgetIdRef.current) {
          window.turnstile.remove(widgetIdRef.current);
        }
      } catch (_) { /* ignore */ }
      widgetIdRef.current = null;
    };
    // We intentionally don't include onVerify here — re-rendering would reset
    // the widget on every parent state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey, theme]);

  return (
    <div className="flex flex-col items-center gap-2" data-testid={testid}>
      <div ref={wrapRef} />
      {error && (
        <p className="text-xs text-red-600 text-center" data-testid={`${testid}-error`}>{error}</p>
      )}
    </div>
  );
}
